import * as uuid from 'uuid';

/* eslint-disable func-style */
/**
 * Get input field label for list of types
 * @param {object} template The template prop object for either key or value.
 * @returns {string} user-friendly list of labels
 */
export function getLabelFor({ types }) {
  let labels = [];

  types.forEach((type, index) => {
    if (type === 'ip' && index < types.length - 1) {
      labels.push('IP Address, DHCP');
    } else {
      labels.push(propertyLabels[type]);
    }
  });
  return labels.length <= 1
    ? labels[0]
    : `${labels.slice(0, -1).join(', ')} or ${labels.pop()}`;
}

/**
 * Get form input tag type
 * @param {object} template The template prop object for either key or value.
 * @returns {string}
 */
export const getTypeForComponent = ({ types }) => {
  // eslint-disable-next-line spellcheck/spell-checker
  let uniques = ['boolean', 'textarea', 'number', 'password'];

  // password takes priority even when combined with other types (e.g. ['text', 'password']),
  // since a field holding a password must always be masked regardless of what else it accepts
  if (types.includes('password')) {
    return 'password';
  }

  // defaults to text if types not in the unique list
  // eslint-disable-next-line spellcheck/spell-checker
  return types.length === 1 && uniques.includes(types[0]) ? types[0] : 'text';
};

/**
 * Get value for the object's input field
 * @param {object} value The value prop object for either key or value.
 * @param {number|false} index
 * @returns {object} current value for index
 */
export function getValueOf(value, index) {
  return Array.isArray(value) && index !== false ? value[index] : value;
}

/**
 * Determine if the hash element value is of type hash
 * @param {object} value The template value prop object.
 * @returns {boolean}
 */
export function isHashType({ types }) {
  return types && types.length === 1 && types[0] == 'hash';
}

/**
 * Parse the hash values to into the format expected by hash form,
 * setting empty values to default values when applicable.
 * @param {array|undefined} values the hash values to be parsed or undefined if value empty
 * @param {array} templates the templates with the hash definition of each of the values to be parsed
 * @returns {array} The parsed list of values.
 */
export function parseValueItem(items, templates) {
  items = items || [];

  if (templates[0] && templates[0].key.multiple) {
    // eslint-disable-next-line no-use-before-define
    return parseMultipleValue(items, templates[0]);
  }

  return templates.map((template) => {
    // eslint-disable-next-line no-use-before-define
    return parseSingleValue(items, template);
  });
}

function buildBasicItem(template) {
  return { id: template.id, enabled: true, key: template.key.default || '' };
}

function keepEnabled(isEnabled) {
  // keeps enabled if value true or undefined
  return isEnabled !== false;
}

function populateValue({ default: defaultValue, multiple }) {
  return multiple ? [defaultValue || ''] : defaultValue || '';
}

function getFromItems(items, id, templateKey) {
  let item = items.find((i) => i.id === id);

  if (!item) {
    return;
  }
  return {
    ...item,
    enabled: keepEnabled(item.enabled),
    key:
      templateKey.mutable && !!item.key.trim() ? item.key : templateKey.default,
  };
}

function getItemValue(currValue, template) {
  if (isHashType(template.value)) {
    return parseValueItem(currValue, template.children);
  }
  return typeof currValue === 'undefined'
    ? populateValue(template.value)
    : currValue;
}

function parseMultipleValue(items, template) {
  let baseValue = { id: template.id, key: [], enabled: true, value: [] };

  if (items.length === 0) {
    items = [buildBasicItem(template)];
  }
  items.forEach((item, idx) => {
    if (idx === 1) {
      baseValue.enabled = keepEnabled(item.enabled);
    }
    baseValue.key.push({ value: item.key, id: uuid.v4() });
    baseValue.value.push(getItemValue(item.value, template));
  });
  return [baseValue];
}

function parseSingleValue(items, template) {
  let item =
    getFromItems(items, template.id, template.key) || buildBasicItem(template);

  item.value = getItemValue(item.value, template);
  item.key = { value: item.key, id: uuid.v4() };
  return item;
}
